import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { PortfolioService } from '../../core/services/portfolio.service';
import { ApiService } from '../../core/services/api.service';
import { PORTFOLIO_REPOSITORY } from '../../core/domain/repositories';
import type {
  LandingResolverData,
  SpotifyNowPlayingPayload,
} from '../../shared/types/landing-resolver-data';

// Re-export so existing call sites that import from this module keep working.
export type { LandingResolverData, SpotifyNowPlayingPayload };

/**
 * Hard cap for the parallel forkJoin. Keeps SSR TTFB bounded even if a single
 * upstream stalls — the resolver returns the triple-null fallback instead of
 * blocking the response.
 */
const RESOLVER_TIMEOUT_MS = 800;

/**
 * Functional `ResolveFn` wired to the landing route. Fires `bootstrap`, `github`,
 * and `spotify` in parallel via `forkJoin`, caps the wait at 800ms, and falls back
 * to a triple-null payload on any error so the navigation never stalls.
 *
 * Runs in the SSR injection context so `HttpTransferCache` will serialize the
 * three GET responses into the SSR HTML, eliminating client re-fetches (R5).
 */
export const landingResolver: ResolveFn<LandingResolverData> = () => {
  const portfolio = inject(PortfolioService);
  const api = inject(ApiService);
  const repo = inject(PORTFOLIO_REPOSITORY);

  return forkJoin({
    bootstrap: repo.getBootstrapData(),
    github: portfolio.getGithubRepos(),
    spotify: api.get<SpotifyNowPlayingPayload>('spotify/now-playing'),
  }).pipe(
    timeout(RESOLVER_TIMEOUT_MS),
    catchError(() => of({ bootstrap: null, github: null, spotify: null })),
  );
};
